import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr';
import './style/OfferWiseReport.css';

// Helper to get today's date in YYYY-MM-DD
const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
};
const OfferWiseReport = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [loader, setLoader] = useState(true);
    const [filter, setFilter] = useState({
        startDate: getTodayDate(),
        endDate: getTodayDate()
    });

    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({
        totalClicks: 0,
        totalConversions: 0,
        totalRevenue: 0
    });

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Logic ---
    const fetchReport = async () => {
        setLoading(true);
        const adminToken = getCookie('adminToken');

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchOfferWiseReport',
                adminToken: adminToken || 'demo_token',
                startDate: filter.startDate,
                endDate: filter.endDate
            });

            if (response.data.status === true) {
                processData(response.data.data);
            }
        } catch (error) {
            console.error("Fetch Error", error);

        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    const processData = (data) => {
        setReportData(data);
        const totals = data.reduce((acc, curr) => ({
            totalClicks: acc.totalClicks + parseInt(curr.raw_clicks || 0),
            totalConversions: acc.totalConversions + parseInt(curr.conversions || 0),
            totalRevenue: acc.totalRevenue + parseFloat(curr.revenue || 0)
        }), { totalClicks: 0, totalConversions: 0, totalRevenue: 0 });
        setSummary(totals);
    };

    useEffect(() => {
        const adminToken = getCookie('adminToken');

        const init = async () => {
            try {
                // ✅ 1. Admin Auth Check
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuthAdmin',
                    adminToken
                });

                if (!authRes.data.status) {
                    toast.error(authRes.data.message || 'Admin session expired');
                    navigate('/cbMediaAdmin7/login');
                    return;
                }

                fetchReport();

            } catch (err) {
                toast.error('Admin auth check failed');
                navigate('/cbMediaAdmin7/login');
            }finally{
                setLoader(false);
            }
        };

        init();

    }, [filter.startDate, filter.endDate]);

    // Handle Input Change
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => ({ ...prev, [name]: value }));
    };

    // Helper to trigger date picker programmatically
    const triggerDatePicker = (id) => {
        const element = document.getElementById(id);
        if (element && element.showPicker) {
            element.showPicker();
        } else {
            element.focus();
        }
    };

    const handleViewDetail = (offerId) => {
        const dateRangeStr = `${filter.startDate}_${filter.endDate}`;
        navigate(`/cbMediaAdmin7/reports/performance/${offerId}/${dateRangeStr}`);
    };
    if (loader) return <Loader />;
    return (
        <div className="report-wrapper_owr">
            <Navbar role="Administrator" isAdmin={true} />

            <div className="bg-shape_owr shape-1_owr"></div>
            <div className="bg-shape_owr shape-2_owr"></div>

            <div className="content-container_owr">

                {/* Header & Filter */}
                <div className="report-header_owr">
                    <div className="header-text_owr">
                        <h2>Offer Wise <span className="highlight_owr">Performance</span></h2>
                        <p>Detailed breakdown of campaign metrics</p>
                    </div>

                    <div className="filter-group_owr">

                        {/* Custom Date 1 */}
                        <div className="custom-date-wrapper_owr" onClick={() => triggerDatePicker('start-date-input')}>
                            <span className="date-label_owr">FROM</span>
                            <div className="date-display_owr">
                                <span>{filter.startDate}</span>
                                <div className="calendar-icon_owr">📅</div>
                            </div>
                            {/* Hidden actual input */}
                            <input
                                id="start-date-input"
                                type="date"
                                name="startDate"
                                value={filter.startDate}
                                onChange={handleDateChange}
                                className="hidden-date-input_owr"
                            />
                        </div>

                        <div className="date-separator_owr">to</div>

                        {/* Custom Date 2 */}
                        <div className="custom-date-wrapper_owr" onClick={() => triggerDatePicker('end-date-input')}>
                            <span className="date-label_owr">TO</span>
                            <div className="date-display_owr">
                                <span>{filter.endDate}</span>
                                <div className="calendar-icon_owr">📅</div>
                            </div>
                            <input
                                id="end-date-input"
                                type="date"
                                name="endDate"
                                value={filter.endDate}
                                onChange={handleDateChange}
                                className="hidden-date-input_owr"
                            />
                        </div>

                    </div>
                </div>

                {/* Summary Cards */}
                <div className="summary-row_owr">
                    <div className="sum-card_owr">
                        <span>Total Clicks</span>
                        <strong>{summary.totalClicks.toLocaleString()}</strong>
                    </div>
                    <div className="sum-card_owr">
                        <span>Total Conversions</span>
                        <strong className="success-text_owr">{summary.totalConversions.toLocaleString()}</strong>
                    </div>
                    <div className="sum-card_owr">
                        <span>Total Revenue</span>
                        <strong className="earn-text_owr">₹{summary.totalRevenue.toLocaleString()}</strong>
                    </div>
                </div>

                {/* Table Section */}
                <div className="table-card_owr">
                    {loading ? (
                        <div className="table-loader_owr">
                            <div className="spinner_owr"></div>
                            <p>Analyzing Performance...</p>
                        </div>
                    ) : (
                        <div className="table-responsive_owr">
                            <table className="report-table_owr">
                                <thead>
                                    <tr>
                                        <th>Offer ID</th>
                                        <th>Offer Name</th>
                                        <th className="text-center_owr">Unique Clicks</th>
                                        <th className="text-center_owr">Raw Clicks</th>
                                        <th className="text-center_owr">Conv.</th>
                                        <th className="text-right_owr">Payout</th>
                                        <th className="text-right_owr">Revenue</th>
                                        <th className="text-center_owr">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.length > 0 ? (
                                        reportData.map((row, index) => (
                                            <tr key={index} className="table-row_owr">
                                                <td className="id-col_owr">{row.offer_id}</td>
                                                <td>
                                                    <div className="offer-flex_owr">
                                                        <img src={row.logo} alt="" className="table-logo_owr" />
                                                        <span>{row.name}</span>
                                                    </div>
                                                </td>
                                                <td className="text-center_owr">{row.unique_clicks}</td>
                                                <td className="text-center_owr muted_owr">{row.raw_clicks}</td>
                                                <td className="text-center_owr success-text_owr font-bold_owr">{row.conversions}</td>
                                                <td className="text-right_owr">₹{row.payout}</td>
                                                <td className="text-right_owr font-bold_owr">₹{row.revenue.toLocaleString()}</td>
                                                <td className="text-center_owr">
                                                    <button
                                                        className="action-btn-black_owr"
                                                        onClick={() => handleViewDetail(row.offer_id)}
                                                    >
                                                        VIEW REPORT
                                                        <div className="wave_owr"></div>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="no-data_owr">No performance data found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default OfferWiseReport;